import React, { useEffect, useRef, useState } from "react";
import useRevealSequence from "../useRevealSequence";
import Celebration from "../Celebration";

/**
 * Spiral Path - the board is drawn into the core.
 *
 * A glowing core sits in the middle with the yantras circling it. When
 * the round ends they all spiral inward, turning as they fall and
 * shrinking as they near the middle, until the core swallows them and
 * opens again as the winner.
 *
 * Laid out after docs/animation-samples/Spiral_Path.html: the cards share
 * one angle so the ring turns as a body, and each finishes at its own
 * small radius so they cluster around the core rather than stack on it.
 */

const RX = 295;
const RY = 245;
const CX = 410;
const CY = 380;
const SWIRL_SPEED = 62; // degrees a second while it waits
const SPIRAL_MS = 800;
const SPIRAL_TURNS = 5;

const easeOutQuart = (p) => 1 - Math.pow(1 - p, 4);

export default function SpiralPath({ tiles, winnerIndex, onFinished }) {
  const count = tiles.length;
  const step = 360 / Math.max(1, count);

  const [spin, setSpin] = useState(0);
  const [progress, setProgress] = useState(0); // 0 = ring, 1 = in the core
  const [target, setTarget] = useState(-1);
  const [landed, setLanded] = useState(-1);
  const spinRef = useRef(0);
  const rafRef = useRef(null);
  // The swirl and the collapse both drive the same angle, so the swirl
  // is told to stop before the collapse starts.
  const freeSpinRef = useRef(true);

  useEffect(() => {
    let last = performance.now();
    const loop = (now) => {
      if (!freeSpinRef.current) return;
      const dt = Math.min(64, now - last);
      last = now;
      spinRef.current = (spinRef.current + (dt / 1000) * SWIRL_SPEED) % 360;
      setSpin(spinRef.current);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useRevealSequence(
    async (ctx) => {
      const found = await ctx.waitForWinner();
      setTarget(found);
      freeSpinRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      const from = spinRef.current;
      const started = performance.now();
      // A browser stops calling requestAnimationFrame when its tab or
      // window is not in front, so a landing driven by frames alone hangs
      // there for ever - and a show that never ends never takes itself off
      // the board. The wait below is raced against a plain timer, and the
      // finished state is set either way.
      const spiral = new Promise((resolve) => {
        const frame = (now) => {
          const p = Math.min(1, (now - started) / SPIRAL_MS);
          setProgress(easeOutQuart(p));
          // Turns on the way in, so it reads as a spiral and not a fall.
          setSpin(from + p * 360 * SPIRAL_TURNS);
          if (p < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });
      await Promise.race([spiral, ctx.sleep(SPIRAL_MS + 400)]);
      setProgress(1);

      setLanded(found);
      await ctx.sleep(1200);
      onFinished();
    },
    winnerIndex,
    [count],
  );

  const winner = landed >= 0 ? tiles[landed] : null;

  return (
    <div className="rv-spiral">
      <div className="rv-spiral-guide" />
      <div className="rv-spiral-glow" />

      {tiles.map((tile, i) => {
        const a = ((i * step - 90 + spin) * Math.PI) / 180;
        // Out on the ring, in to a nest of its own around the core.
        const radius = (1 - progress) * RX + progress * (26 + i * 1.8);
        const radiusY = (1 - progress) * RY + progress * (26 + i * 1.8);
        const x = CX + Math.cos(a) * radius;
        const y = CY + Math.sin(a) * radiusY;

        return (
          <div
            key={tile.id}
            className={[
              "rv-card rv-card--float",
              i === target && progress > 0.55 && landed < 0
                ? "rv-card--active"
                : "",
              landed >= 0 ? "rv-card--gone" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              left: `${x}px`,
              top: `${y}px`,
              transform: `translate(-50%, -50%) scale(${1 - progress * 0.45})`,
              opacity: landed >= 0 ? 0 : 1,
            }}
          >
            <img src={tile.img} alt={tile.name || ""} />
            <span>{tile.name}</span>
          </div>
        );
      })}

      {/* The core holds the board while it falls in, then opens as the
          winner. */}
      <div className={`rv-spiral-core ${winner ? "rv-spiral-core--won" : ""}`}>
        {winner ? (
          <>
            <div className="rv-spiral-crown">👑</div>
            <img src={winner.img} alt={winner.name || "winner"} />
            <div className="rv-spiral-winner-name">{winner.name}</div>
          </>
        ) : (
          <div className="rv-spiral-core-label">Spiral Core</div>
        )}
      </div>

      {winner ? <Celebration tile={winner} seed={landed} compact /> : null}
    </div>
  );
}
